// @flow

import ListPage from 'esm-web-pro/containers/pages/ListPage';
import { More, Trash } from 'grommet-icons';
import React from 'react';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import { Route, Switch } from 'react-router';
import { BooleanColumn } from 'speed-js-react-web';
import ActionsColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import EditIcon from 'esm-web-pro/components/icons/EditIcon';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';
import RolesSidePanel from 'esm-web-pro/components/pages/roles/RolesSidePanel';
import RolesDashboard from 'esm-web-pro/components/pages/roles/RolesDashboard';
import UsersSidePanel, { USERS_SIDEPANEL_KEY } from 'esm-web-pro/components/pages/users/UsersSidePanel';

type Props = {
    intl: IntlShape,
}

const ROLES_SIDEPANEL_KEY = 'ROLES_SIDEPANEL_KEY';

const messages = defineMessages({
    roles: {
        id: 'authorization-role',
        defaultMessage: 'Roles',
    },
    users: {
        id: 'authorization-users',
        defaultMessage: 'Users',
    },
    permissions: {
        id: 'authorization-permissions',
        defaultMessage: 'Permissions',
    },
    groups: {
        id: 'authorization-groups',
        defaultMessage: 'Groups',
    },
});

const AuthorizationRoutes = (authorizationProps: Props) => {
    const { intl: { formatMessage } } = authorizationProps;

    const listRef = React.useRef(null);

    const reloadRolesTable = () => {
        if (listRef.current && listRef.current.table.current) {
            listRef.current.table.current.reloadData();
        }
    };
    const ref = React.useRef(null);

    const reloadUsersTable = () => {
        if (ref.current && ref.current.table.current) {
            ref.current.table.current.reloadData();
        }
    };

    return (
        <>
            <Switch>
                <Route
                    path="/administration/authorisation/roles/:id/dashboard"
                    render={() => (
                        <RolesDashboard />
                    )}
                />
                <Route
                    path="/administration/authorisation/roles"
                    render={(props) => (
                        <ListPage
                            {...props}
                            ref={listRef}
                            resourceName="role"
                            permissions={['authorisation.view_role']}
                            title={formatMessage(messages.roles)}
                            columns={[
                                {
                                    Header: <FormattedMessage id="authorization-role-name" defaultMessage="Name" />,
                                    accessor: 'name',
                                },
                                {
                                    Header: <FormattedMessage id="authorization-role-code" defaultMessage="Code" />,
                                    accessor: 'code',
                                },
                                {
                                    actions: [{
                                        label: formatMessage(actionMessages.dashboard),
                                        to: '/administration/authorisation/roles/{uuid}/dashboard',
                                        type: 'link',
                                        icon: <More />,
                                    }, {
                                        label: <FormattedMessage
                                            id="projmanag-counterparties-quickview"
                                            defaultMessage="Quick View"
                                        />,
                                        type: 'quickView',
                                        accessor: 'uuid',
                                        component: RolesSidePanel,
                                        resourceName: 'role',
                                        icon: <QuickViewIcon />,
                                        to: '/{id}/quick_view',
                                        getTitle: (obj) => obj.name,
                                        panelId: ROLES_SIDEPANEL_KEY,
                                        reload: reloadRolesTable,
                                    }],
                                    Cell: ActionsColumn,
                                    minWidth: 145,
                                },
                            ]}
                            newObjectUrl="/administration/authorisation/roles/new"
                            newObjectLabel={(
                                <FormattedMessage
                                    id="authorization-role-newrole"
                                    defaultMessage="New Role"
                                />
                            )}
                            editObjectLabel={(
                                <FormattedMessage
                                    id="authorization-role-editrole"
                                    defaultMessage="Edit Role"
                                />
                            )}
                        />
                    )}
                />
            </Switch>
            <Route
                path="/administration/authorisation/users"
                render={(props) => (
                    <ListPage
                        ref={ref}
                        {...props}
                        resourceName="user"
                        title={formatMessage(messages.users)}
                        permissions={['authorisation.view_user']}
                        columns={[
                            {
                                Header: <FormattedMessage
                                    id="authorization-users-username"
                                    defaultMessage="Username"
                                />,
                                accessor: 'username',
                            },
                            {
                                Header: <FormattedMessage
                                    id="authorization-users-isactive"
                                    defaultMessage="Is active"
                                />,
                                accessor: 'is_active',
                                orderBy: 'user_systems__is_active',
                                Cell: BooleanColumn,
                            },
                            {
                                Header: <FormattedMessage id="authorization-users-systems" defaultMessage="Systems" />,
                                id: 'systems',
                                accessor: (d) => [...new Set(d.systems?.map((ss) => ss.system.name))].join(', '),
                            },
                            {
                                Header: <FormattedMessage id="authorization-users-isstaff" defaultMessage="Is staff" />,
                                accessor: 'is_staff',
                                Cell: BooleanColumn,
                            },
                            {
                                Header: <FormattedMessage
                                    id="authorization-users-organisationalunit"
                                    defaultMessage="Organisational Unit"
                                />,
                                Cell: (rowProps) => {
                                    const { original: { org_unit: orgUnits } } = rowProps;
                                    return orgUnits && orgUnits.map((ou) => ou.name).join(', ');
                                },
                            },
                            {
                                actions: [
                                    {
                                        label: <FormattedMessage
                                            id="projmanag-counterparties-quickview"
                                            defaultMessage="Quick View"
                                        />,
                                        type: 'quickView',
                                        accessor: 'uuid',
                                        component: UsersSidePanel,
                                        resourceName: 'user',
                                        icon: <QuickViewIcon />,
                                        getTitle: (obj) => obj.username,
                                        panelId: USERS_SIDEPANEL_KEY,
                                        reload: reloadUsersTable,
                                    },
                                ],
                                Cell: ActionsColumn,
                                minWidth: 145,
                            },
                        ]}
                        newObjectUrl="/administration/authorisation/users/new"
                        newObjectLabel={<FormattedMessage id="authorization-users-newuser" defaultMessage="New User" />}
                        editObjectLabel={(
                            <FormattedMessage
                                id="authorization-users-edituser"
                                defaultMessage="Edit User"
                            />
                        )}
                    />
                )}
            />
            <Route
                path="/administration/authorisation/permissions"
                permissions={['auth.view_permission']}
                render={(props) => (
                    <ListPage
                        {...props}
                        resourceName="permission"
                        title={formatMessage(messages.permissions)}
                        permissions={['auth.view_permission']}
                        columns={[
                            {
                                Header: <FormattedMessage id="authorization-permissions-name" defaultMessage="Name" />,
                                accessor: 'name',
                            },
                            {
                                Header: <FormattedMessage
                                    id="authorization-permissions-codename"
                                    defaultMessage="Code name"
                                />,
                                accessor: 'codename',
                            },
                        ]}
                    />
                )}
            />
            <Route
                path="/administration/authorisation/groups"
                render={(props) => (
                    <ListPage
                        {...props}
                        resourceName="group"
                        title={formatMessage(messages.groups)}
                        permissions={['authorisation.view_group']}
                        columns={[
                            {
                                Header: <FormattedMessage id="authorization-groups-name" defaultMessage="Name" />,
                                accessor: 'name',
                            },
                            {
                                actions: [{
                                    label: formatMessage(actionMessages.edit),
                                    to: '/administration/authorisation/groups/update/{uuid}',
                                    type: 'link',
                                    icon: <EditIcon />,
                                }, {
                                    label: formatMessage(actionMessages.delete),
                                    resourceName: 'group',
                                    accessor: 'uuid',
                                    type: 'delete',
                                    icon: <Trash />,
                                }],
                                Cell: ActionsColumn,
                                minWidth: 160,
                            },
                        ]}
                        newObjectUrl="/administration/authorisation/groups/new"
                        newObjectLabel={(
                            <FormattedMessage
                                id="authorization-groups-newgroup"
                                defaultMessage="New Group"
                            />
                        )}
                        editObjectLabel={(
                            <FormattedMessage
                                id="authorization-groups-editgroup"
                                defaultMessage="Edit Group"
                            />
                        )}
                    />
                )}
            />
        </>
    );
};

export default injectIntl(AuthorizationRoutes);
